import { type ReactNode } from "react";
import styled from "@emotion/styled";
import { Spinner } from "../../feedback/Spinner";

export type ChatBubbleProps = {
  sender: "user" | "assistant" | "system";
  children?: ReactNode;
  eyebrow?: ReactNode;
  title?: ReactNode;
  timestamp?: ReactNode;
  status?: ReactNode;
  loading?: boolean;
  errorAction?: ReactNode;
  className?: string;
};

type ChatSender = "user" | "assistant" | "system";

/* Exact compiled output of the pre-migration Tailwind utilities. The
 * pre-existing dead class `bfrs-[word-break:break-word]` (never emitted by
 * Tailwind — the prefix breaks arbitrary-property syntax) is intentionally
 * not reproduced. Verified against the frozen baseline. */

const ChatBubbleRoot = styled("div", {
  label: "AgenticChatBubble-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { sender: ChatSender } }>(({ ownerState }) => [
  {
    display: "flex",
    width: "100%"
  },
  ownerState.sender === "user" ? { justifyContent: "flex-end" } : { justifyContent: "flex-start" }
]);

const ChatBubblePanel = styled("div", {
  label: "AgenticChatBubble-panel",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { sender: ChatSender } }>(({ ownerState }) => [
  { maxWidth: "min(84%,40rem)" },
  ownerState.sender !== "assistant" && {
    borderRadius: "var(--bfrs-radius-2xl)",
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: ".75rem",
    paddingBottom: ".75rem",
    fontSize: ".875rem",
    lineHeight: "1.5rem",
    "--tw-shadow": "var(--bfrs-shadow-low)",
    "--tw-shadow-colored": "var(--bfrs-shadow-low)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  },
  ownerState.sender === "user" && {
    borderBottomRightRadius: "var(--bfrs-radius-sm)",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.sender === "assistant" && {
    maxWidth: "min(100%,23.125rem)",
    textAlign: "left"
  },
  ownerState.sender === "system" && {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  }
]);

const ChatBubbleAssistantStack = styled("div", {
  label: "AgenticChatBubble-assistantStack"
})({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: ".5rem"
});

const ChatBubbleEyebrow = styled("div", {
  label: "AgenticChatBubble-eyebrow"
})({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  justifyContent: "center",
  fontSize: "10px",
  fontWeight: 700,
  textTransform: "uppercase",
  lineHeight: "1.5",
  letterSpacing: "1.6px",
  color: "var(--bfrs-dcl-color-chat-assistant-eyebrow)"
});

const ChatBubbleTitleBlock = styled("div", {
  label: "AgenticChatBubble-titleBlock"
})({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: ".5rem"
});

const ChatBubbleTitle = styled("div", {
  label: "AgenticChatBubble-title"
})({
  width: "100%",
  fontSize: "20px",
  fontWeight: 600,
  lineHeight: "1.5",
  color: "var(--bfrs-dcl-color-chat-assistant-title)"
});

const ChatBubbleBody = styled("div", {
  label: "AgenticChatBubble-body"
})({
  maxWidth: "369px",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "17px",
  color: "var(--bfrs-dcl-color-chat-assistant-body)"
});

const ChatBubbleMeta = styled("div", {
  label: "AgenticChatBubble-meta"
})({
  marginTop: ".5rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: ".75rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

export function ChatBubble({
  sender,
  children,
  eyebrow,
  title,
  timestamp,
  status,
  loading,
  errorAction,
  className
}: ChatBubbleProps) {
  const isAssistant = sender === "assistant";

  const content = loading ? (
    <Spinner size="sm" />
  ) : isAssistant ? (
    <ChatBubbleAssistantStack>
      {eyebrow && <ChatBubbleEyebrow>{eyebrow}</ChatBubbleEyebrow>}
      {(title || children) && (
        <ChatBubbleTitleBlock>
          {title && <ChatBubbleTitle>{title}</ChatBubbleTitle>}
          {children && <ChatBubbleBody>{children}</ChatBubbleBody>}
        </ChatBubbleTitleBlock>
      )}
    </ChatBubbleAssistantStack>
  ) : (
    children
  );

  return (
    <ChatBubbleRoot ownerState={{ sender }} className={className}>
      <ChatBubblePanel ownerState={{ sender }}>
        {content}
        {(timestamp || status || errorAction) && (
          <ChatBubbleMeta>
            <span>{timestamp}</span>
            <span>{errorAction ?? status}</span>
          </ChatBubbleMeta>
        )}
      </ChatBubblePanel>
    </ChatBubbleRoot>
  );
}
